import { isArray } from '../array.js';
import type { CalendarConfig, DurationConfig, DateAdjustmentConfig, RelativeGradeConfig, AssessmentConfig, ReportCardConfig, GradeAverageConfig } from '../calculation-core/contracts.js';
import type { ToolDefinition, ToolConfig, ToolId, RegistrySource } from '../../types/tool-registry.js';
import type { Calculation, CoreMetadata } from '../../types/calculation-core.js';
import type { DateTimeValue } from '../../../source/shared/date-types.js';
import type { EducationValue } from '../../../source/shared/education-types.js';
interface Lifecycle {
    readonly configVersion: string;
    readonly effectiveFrom: string;
    readonly lastReviewed: string;
    readonly reviewAfter: string | null;
}
interface Options {
    readonly currentDate: string;
}
interface FrozenContract {
    readonly family: 'date-time' | 'education';
    readonly path: string;
    readonly categoryId: string;
    readonly classification: string;
    readonly engineId: string;
    readonly configKind: ToolConfig['kind'];
    readonly calculatorId: string;
    readonly testVectorKind: string;
    readonly configVersion: string;
    readonly schemaVersion: string;
    readonly configKeys: readonly string[];
    readonly input: readonly (readonly [
        string,
        string,
        boolean
    ])[];
    readonly result: readonly (readonly [
        string,
        string,
        boolean
    ])[];
}
import { calculateAge, calculateDateAdjustment, calculateDateDifference, calculateDuration, calculateGradeAverage, calculateIsoWeek, calculateRelativeSystemGrade, calculateReportCardAverage, calculateRequiredFinalScore, } from '@hephesapla/calculation-core';
const frozenToolContracts: Readonly<Partial<Record<ToolId, FrozenContract>>> = {
    age: {
        family: 'date-time', path: '/tarih-zaman/yas-hesaplama', categoryId: 'tarih-zaman', classification: 'timeless',
        engineId: 'age-v1', configKind: 'calendar-date-v1', calculatorId: 'tarih-zaman.yas-hesaplama',
        testVectorKind: 'date-time-oracle-v1', configVersion: '1.0.0', schemaVersion: '1.0.0',
        configKeys: ['calculatorId', 'kind', 'maxYear', 'minYear'],
        input: [['birthDate', 'date', true], ['referenceDate', 'date', true]],
        result: [['birthDate', 'text', false], ['referenceDate', 'text', false], ['years', 'number', false], ['months', 'number', false], ['days', 'number', false], ['totalDays', 'number', false], ['totalHours', 'number', false], ['totalMinutes', 'number', false], ['nextBirthday', 'text', true], ['daysUntilNextBirthday', 'number', true]],
    },
    'date-difference': {
        family: 'date-time', path: '/tarih-zaman/iki-tarih-arasi-fark-hesaplama', categoryId: 'tarih-zaman', classification: 'timeless',
        engineId: 'date-difference-v1', configKind: 'calendar-date-v1', calculatorId: 'tarih-zaman.iki-tarih-arasi-fark-hesaplama',
        testVectorKind: 'date-time-oracle-v1', configVersion: '1.0.0', schemaVersion: '1.0.0',
        configKeys: ['calculatorId', 'kind', 'maxYear', 'minYear'],
        input: [['startDate', 'date', true], ['endDate', 'date', true]],
        result: [['startDate', 'text', false], ['endDate', 'text', false], ['years', 'number', false], ['months', 'number', false], ['days', 'number', false], ['totalDays', 'number', false], ['totalWeeks', 'number', false], ['totalHours', 'number', false], ['totalMinutes', 'number', false]],
    },
    duration: {
        family: 'date-time', path: '/tarih-zaman/saat-sure-hesaplama', categoryId: 'tarih-zaman', classification: 'timeless',
        engineId: 'duration-v1', configKind: 'duration-v1', calculatorId: 'tarih-zaman.saat-sure-hesaplama',
        testVectorKind: 'date-time-oracle-v1', configVersion: '1.0.0', schemaVersion: '1.0.0',
        configKeys: ['calculatorId', 'kind', 'maxHours', 'maxMinutes'],
        input: [['operation', 'select', true], ['firstHours', 'number', true], ['firstMinutes', 'number', true], ['secondHours', 'number', true], ['secondMinutes', 'number', true]],
        result: [['operation', 'text', false], ['first', 'object', false], ['second', 'object', false], ['hours', 'number', false], ['minutes', 'number', false], ['signedTotalMinutes', 'number', false], ['absoluteTotalMinutes', 'number', false], ['decimalHours', 'number', false], ['isNegative', 'boolean', false]],
    },
    'date-adjustment': {
        family: 'date-time', path: '/tarih-zaman/tarihe-gun-ekleme-cikarma-hesaplama', categoryId: 'tarih-zaman', classification: 'timeless',
        engineId: 'date-adjustment-v1', configKind: 'date-adjustment-v1', calculatorId: 'tarih-zaman.tarihe-gun-ekleme-cikarma-hesaplama',
        testVectorKind: 'date-time-oracle-v1', configVersion: '1.0.0', schemaVersion: '1.0.0',
        configKeys: ['calculatorId', 'kind', 'maxDays', 'maxMonths', 'maxWeeks', 'maxYear', 'maxYears', 'minYear'],
        input: [['operation', 'select', true], ['startDate', 'date', true], ['years', 'number', true], ['months', 'number', true], ['weeks', 'number', true], ['days', 'number', true]],
        result: [['operation', 'text', false], ['startDate', 'text', false], ['years', 'number', false], ['months', 'number', false], ['weeks', 'number', false], ['days', 'number', false], ['resultDate', 'text', false], ['totalDays', 'number', false], ['isoWeekday', 'number', false], ['dayOfYear', 'number', false]],
    },
    'iso-week': {
        family: 'date-time', path: '/tarih-zaman/hafta-hesaplama', categoryId: 'tarih-zaman', classification: 'timeless',
        engineId: 'iso-week-v1', configKind: 'calendar-date-v1', calculatorId: 'tarih-zaman.hafta-hesaplama',
        testVectorKind: 'date-time-oracle-v1', configVersion: '1.0.0', schemaVersion: '1.0.0',
        configKeys: ['calculatorId', 'kind', 'maxYear', 'minYear'],
        input: [['date', 'date', true]],
        result: [['date', 'text', false], ['isoWeek', 'number', false], ['isoYear', 'number', false], ['isoWeekday', 'number', false], ['dayOfYear', 'number', false], ['weekStart', 'text', true], ['weekEnd', 'text', true]],
    },
    'relative-grade': {
        family: 'education', path: '/egitim-sinav/bagil-sistem-not-hesaplama', categoryId: 'egitim-sinav', classification: 'remote-config',
        engineId: 'relative-system-grade-v1', configKind: 'relative-system-grade-v1', calculatorId: 'egitim-sinav.bagil-sistem-not-hesaplama',
        testVectorKind: 'education-oracle-v1', configVersion: '1.0.0', schemaVersion: '1.0.0',
        configKeys: ['calculatorId', 'gradeScenario', 'kind', 'maxScore', 'maxStandardDeviation', 'tScoreCenter', 'tScoreScale'],
        input: [['score', 'number', true], ['groupMean', 'number', true], ['standardDeviation', 'number', true]],
        result: [['score', 'number', false], ['groupMean', 'number', false], ['standardDeviation', 'number', false], ['zScore', 'number', false], ['tScore', 'number', false], ['grade', 'object', false], ['scenario', 'object', false]],
    },
    'report-card-average': {
        family: 'education', path: '/egitim-sinav/karne-notu-hesaplama', categoryId: 'egitim-sinav', classification: 'timeless',
        engineId: 'report-card-average-v1', configKind: 'report-card-average-v1', calculatorId: 'egitim-sinav.karne-notu-hesaplama',
        testVectorKind: 'education-oracle-v1', configVersion: '1.0.0', schemaVersion: '1.0.0',
        configKeys: ['calculatorId', 'kind', 'maxCourses', 'maxScore', 'maxWeeklyHours'],
        input: [['courses', 'object-list', true]],
        result: [['courses', 'object-list', false], ['courseCount', 'number', false], ['totalWeeklyHours', 'number', false], ['weightedPointTotal', 'number', false], ['weightedAverage', 'number', false]],
    },
    'grade-average': {
        family: 'education', path: '/egitim-sinav/not-ortalamasi-hesaplama', categoryId: 'egitim-sinav', classification: 'remote-config',
        engineId: 'grade-average-v2', configKind: 'grade-average-v2', calculatorId: 'egitim-sinav.not-ortalamasi-hesaplama',
        testVectorKind: 'education-oracle-v1', configVersion: '2.0.0', schemaVersion: '2.0.0',
        configKeys: ['calculatorId', 'kind', 'maxCourses', 'maxCredits', 'maxScore', 'unweightedBandScenario', 'weightedGradeScenario'],
        input: [['mode', 'select', true], ['averageMode', 'select', false], ['courses', 'object-list', false], ['score', 'number', false], ['threshold', 'number', false]],
        result: [['mode', 'text', false], ['averageMode', 'text', true], ['courses', 'object-list', true], ['courseCount', 'number', true], ['totalWeight', 'number', true], ['weightedPointTotal', 'number', true], ['average100', 'number', true], ['gradePointAverage', 'number', true], ['score', 'number', true], ['threshold', 'number', true], ['differenceFromThreshold', 'number', true], ['meetsThreshold', 'boolean', true], ['classification', 'object', false], ['scenario', 'object', false]],
    },
    'required-final-score': {
        family: 'education', path: '/egitim-sinav/vize-final-gecme-notu-hesaplama', categoryId: 'egitim-sinav', classification: 'timeless',
        engineId: 'required-final-score-v2', configKind: 'weighted-assessment-v2', calculatorId: 'egitim-sinav.vize-final-gecme-notu-hesaplama',
        testVectorKind: 'education-oracle-v1', configVersion: '2.0.0', schemaVersion: '2.0.0',
        configKeys: ['calculatorId', 'kind', 'maxScore', 'weightTolerance', 'weightTotal'],
        input: [['mode', 'select', true], ['observedMidtermScore', 'number', false], ['observedFinalScore', 'number', false], ['observedMidtermWeightPercent', 'number', false], ['observedFinalWeightPercent', 'number', false], ['midtermScore', 'number', false], ['targetAverage', 'number', false], ['midtermWeightPercent', 'number', false], ['finalWeightPercent', 'number', false], ['examScore', 'number', false], ['threshold', 'number', false], ['priorScore', 'number', false], ['priorWeightPercent', 'number', false]],
        result: [['mode', 'text', false], ['observedMidtermScore', 'number', true], ['observedFinalScore', 'number', true], ['observedMidtermWeightPercent', 'percentage', true], ['observedFinalWeightPercent', 'percentage', true], ['observedMidtermContribution', 'number', true], ['observedFinalContribution', 'number', true], ['weightedTotal', 'number', true], ['weightTotal', 'percentage', true], ['midtermScore', 'number', true], ['targetAverage', 'number', true], ['midtermWeightPercent', 'percentage', true], ['finalWeightPercent', 'percentage', true], ['midtermContribution', 'number', true], ['requiredFinalScore', 'number', true], ['status', 'text', true], ['thresholdMode', 'text', true], ['examScore', 'number', true], ['threshold', 'number', true], ['priorScore', 'number', true], ['priorWeightPercent', 'percentage', true], ['examWeightPercent', 'percentage', true], ['evaluatedScore', 'number', true], ['differenceFromThreshold', 'number', true], ['meetsThreshold', 'boolean', true]],
    },
};
const strictSemverPattern = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
const strictIsoDatePattern = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const strictHttpsUrlPattern = /^https:\/\/(?=[^/?#\s]*[a-z0-9])[^/@\s?#]+(?:[/?#][^\s]*)?$/i;
const sourceKeys = [
    'kind',
    'publisher',
    'reviewedOn',
    'sourceGroupId',
    'sourceId',
    'title',
    'url',
    'watchFields',
];
const sourceKinds = new Set([
    'official-government',
    'official-operator',
    'authoritative-education',
    'international-standards-body',
]);
function isValidIsoDate(value: unknown): value is string {
    if (typeof value !== 'string' || !strictIsoDatePattern.test(value))
        return false;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
}
function requireIsoDate(value: unknown, label: string): string {
    if (!isValidIsoDate(value))
        throw new Error(`${label} geçerli YYYY-AA-GG tarihi olmalıdır.`);
    return value;
}
function compareStrictSemver(left: string, right: string) {
    const leftMatch = strictSemverPattern.exec(left);
    const rightMatch = strictSemverPattern.exec(right);
    if (!leftMatch || !rightMatch)
        return null;
    for (let index = 1; index <= 3; index += 1) {
        const leftPart = BigInt(leftMatch[index]);
        const rightPart = BigInt(rightMatch[index]);
        if (leftPart < rightPart)
            return -1;
        if (leftPart > rightPart)
            return 1;
    }
    return 0;
}
function sortedKeys(value: object) {
    return Object.keys(value).sort();
}
function sameProjection(left: unknown, right: unknown) {
    return JSON.stringify(left) === JSON.stringify(right);
}
function inputProjection(tool: ToolDefinition) {
    return tool.inputSchema.fields.map((field) => [field.id, field.type, field.required]);
}
function resultProjection(tool: ToolDefinition) {
    return tool.resultSchema.fields.map((field) => [field.id, field.type, field.nullable]);
}
function contractFor(tool: ToolDefinition, family: 'date-time' | 'education') {
    const contract = frozenToolContracts[tool.toolId];
    if (!contract || contract.family !== family) {
        throw new Error(`${tool.toolId} ${family} runtime adapterı tarafından desteklenmiyor.`);
    }
    if (tool.path !== contract.path ||
        tool.category.id !== contract.categoryId ||
        tool.classification !== contract.classification ||
        tool.engineId !== contract.engineId ||
        tool.schemaVersion !== contract.schemaVersion ||
        tool.config.kind !== contract.configKind ||
        tool.testVectors.kind !== contract.testVectorKind) {
        throw new Error(`${tool.toolId} frozen runtime sözleşmesiyle eşleşmiyor.`);
    }
    const versionOrder = compareStrictSemver(tool.configVersion, contract.configVersion);
    if (versionOrder === null ||
        (contract.classification === 'timeless' && versionOrder !== 0) ||
        (contract.classification === 'remote-config' && versionOrder < 0)) {
        throw new Error(contract.classification === 'timeless'
            ? `${tool.toolId}.configVersion zamansız araç için exact ${contract.configVersion} olmalıdır.`
            : `${tool.toolId}.configVersion strict semver ve baseline ${contract.configVersion} veya yeni olmalıdır.`);
    }
    const calculatorId = 'calculatorId' in tool.config ? tool.config.calculatorId : null;
    if (calculatorId !== contract.calculatorId) {
        throw new Error(`${tool.toolId} canonical calculatorId eşleşmiyor.`);
    }
    if (!sameProjection(sortedKeys(tool.config), contract.configKeys)) {
        throw new Error(`${tool.toolId} config alan sözleşmesi eşleşmiyor.`);
    }
    if (!sameProjection(inputProjection(tool), contract.input)) {
        throw new Error(`${tool.toolId} input şeması frozen sözleşmeyle eşleşmiyor.`);
    }
    if (!sameProjection(resultProjection(tool), contract.result)) {
        throw new Error(`${tool.toolId} result şeması frozen sözleşmeyle eşleşmiyor.`);
    }
    return contract;
}
function metadataFor(tool: ToolDefinition, contract: FrozenContract, lifecycle: Lifecycle): CoreMetadata {
    return {
        calculatorId: contract.calculatorId,
        version: lifecycle.configVersion,
        effectiveFrom: lifecycle.effectiveFrom,
        ...(tool.effectiveTo ? { effectiveTo: tool.effectiveTo } : {}),
        lastVerifiedAt: lifecycle.lastReviewed,
        sources: tool.sources.map((source) => ({
            id: source.sourceId,
            title: source.title,
            publisher: source.publisher,
            url: source.url,
            verifiedAt: source.reviewedOn,
        })),
        notes: tool.content.limitations,
    };
}
function baselineLifecycle(tool: ToolDefinition) {
    return {
        configVersion: tool.configVersion,
        effectiveFrom: tool.effectiveFrom,
        lastReviewed: tool.lastReviewed,
        reviewAfter: tool.updateCadence.reviewAfter,
    };
}
function assertLifecycleShape(lifecycle: Lifecycle, label: string) {
    if (!strictSemverPattern.test(lifecycle.configVersion)) {
        throw new Error(`${label}.configVersion strict major.minor.patch semver olmalıdır.`);
    }
    requireIsoDate(lifecycle.effectiveFrom, `${label}.effectiveFrom`);
    requireIsoDate(lifecycle.lastReviewed, `${label}.lastReviewed`);
    if (lifecycle.reviewAfter !== null)
        requireIsoDate(lifecycle.reviewAfter, `${label}.reviewAfter`);
    if (lifecycle.lastReviewed < lifecycle.effectiveFrom) {
        throw new Error(`${label}.lastReviewed effectiveFrom tarihinden önce olamaz.`);
    }
    if (lifecycle.reviewAfter !== null && lifecycle.reviewAfter <= lifecycle.lastReviewed) {
        throw new Error(`${label}.reviewAfter lastReviewed tarihinden sonra olmalıdır.`);
    }
}
function assertToolProvenance(tool: ToolDefinition, lifecycle: Lifecycle) {
    if (!isArray(tool.sources) || tool.sources.length === 0) {
        throw new Error(`${tool.toolId}.sources en az bir kaynak taşımalıdır.`);
    }
    const sourceIds = new Set();
    const sources: readonly RegistrySource[] = tool.sources;
    for (const [index, source] of sources.entries()) {
        const label = `${tool.toolId}.sources.${index}`;
        if (!sameProjection(sortedKeys(source), sourceKeys)) {
            throw new Error(`${label} alanları kapalı kaynak sözleşmesiyle eşleşmiyor.`);
        }
        if (typeof source.sourceId !== 'string' || source.sourceId.trim() === '' ||
            typeof source.title !== 'string' || source.title.trim() === '' ||
            typeof source.publisher !== 'string' || source.publisher.trim() === '' ||
            (source.sourceGroupId !== null &&
                (typeof source.sourceGroupId !== 'string' || source.sourceGroupId.trim() === '')) ||
            !sourceKinds.has(source.kind) ||
            !isArray(source.watchFields) || source.watchFields.length === 0 ||
            source.watchFields.some((field: string) => typeof field !== 'string' || field.trim() === '')) {
            throw new Error(`${label} provenance descriptorı geçersiz.`);
        }
        if (sourceIds.has(source.sourceId)) {
            throw new Error(`${tool.toolId}.sources yinelenen sourceId taşıyor: ${source.sourceId}.`);
        }
        sourceIds.add(source.sourceId);
        const reviewedOn = requireIsoDate(source.reviewedOn, `${label}.reviewedOn`);
        if (reviewedOn > lifecycle.lastReviewed) {
            throw new Error(`${label}.reviewedOn tool lastReviewed tarihinden sonra olamaz.`);
        }
        if (typeof source.url !== 'string' || !strictHttpsUrlPattern.test(source.url)) {
            throw new Error(`${label}.url geçerli HTTPS adresi olmalıdır.`);
        }
    }
}
function assertRemoteLifecycleActive(tool: ToolDefinition, lifecycle: Lifecycle, currentDate: string) {
    const today = requireIsoDate(currentDate, `${tool.toolId}.currentDate`);
    if (today < lifecycle.effectiveFrom || today < lifecycle.lastReviewed) {
        throw new Error(`${tool.toolId} configi ${today} tarihinde henüz aktif değil.`);
    }
    if (tool.effectiveTo !== null && today > tool.effectiveTo) {
        throw new Error(`${tool.toolId} configinin yürürlük dönemi sona erdi.`);
    }
    if (lifecycle.reviewAfter === null) {
        throw new Error(`${tool.toolId} remote config reviewAfter tarihi taşımıyor.`);
    }
    if (tool.toolId !== 'relative-grade' && tool.toolId !== 'grade-average' && today >= lifecycle.reviewAfter) {
        throw new Error(`${tool.toolId} configi ${lifecycle.reviewAfter} tarihinde stale oldu.`);
    }
}
function assertEducationScenarioKind(config: ToolConfig, toolId: ToolId, expectedKind: 'example' | 'institution') {
    const scenarios = config.kind === 'relative-system-grade-v1'
        ? [config.gradeScenario]
        : config.kind === 'grade-average-v2'
            ? [config.weightedGradeScenario, config.unweightedBandScenario]
            : [];
    if (scenarios.length === 0 || scenarios.some((scenario) => scenario.kind !== expectedKind)) {
        throw new Error(expectedKind === 'example'
            ? `${toolId} bundled temel configi yalnız açık example senaryosu taşımalıdır.`
            : `${toolId} güncel kurum configi yalnız açık institution senaryosu taşımalıdır.`);
    }
    for (const scenario of scenarios) {
        if (!sameProjection(sortedKeys(scenario), ['bands', 'id', 'kind', 'label'])) {
            throw new Error(`${toolId} eğitim senaryo alanları kapalı tip sözleşmesiyle eşleşmiyor.`);
        }
        if (typeof scenario.id !== 'string' || scenario.id.trim() === '' ||
            typeof scenario.label !== 'string' || scenario.label.trim() === '' ||
            !isArray(scenario.bands) || scenario.bands.length === 0) {
            throw new Error(`${toolId} eğitim senaryo descriptorı geçersiz.`);
        }
        const bands: readonly {
            readonly minimum: number | null;
            readonly label: string;
            readonly coefficient: number | null;
        }[] = scenario.bands;
        for (const band of bands) {
            if (!sameProjection(sortedKeys(band), ['coefficient', 'label', 'minimum'])) {
                throw new Error(`${toolId} eğitim bant alanları kapalı tip sözleşmesiyle eşleşmiyor.`);
            }
            if ((band.minimum !== null && (typeof band.minimum !== 'number' || !Number.isFinite(band.minimum))) ||
                typeof band.label !== 'string' || band.label.trim() === '' ||
                (band.coefficient !== null &&
                    (typeof band.coefficient !== 'number' || !Number.isFinite(band.coefficient)))) {
                throw new Error(`${toolId} eğitim bant descriptorı geçersiz.`);
            }
        }
    }
}
function educationDefinitionFor(tool: ToolDefinition, contract: FrozenContract, options: Options) {
    if (!options || typeof options !== 'object' || isArray(options) ||
        !sameProjection(sortedKeys(options), ['currentDate'])) {
        throw new Error(`${tool.toolId}.options yalnız currentDate alanını taşımalıdır.`);
    }
    requireIsoDate(options?.currentDate, `${tool.toolId}.currentDate`);
    const lifecycle = baselineLifecycle(tool);
    assertLifecycleShape(lifecycle, `${tool.toolId}.lifecycle`);
    if (tool.effectiveTo !== null) {
        requireIsoDate(tool.effectiveTo, `${tool.toolId}.effectiveTo`);
        if (tool.effectiveTo < lifecycle.effectiveFrom) {
            throw new Error(`${tool.toolId}.effectiveTo effectiveFrom tarihinden önce olamaz.`);
        }
    }
    assertToolProvenance(tool, lifecycle);
    const config = tool.config;
    if (tool.classification === 'timeless') {
        if (lifecycle.reviewAfter !== null) {
            throw new Error(`${tool.toolId} zamansız config reviewAfter taşımamalıdır.`);
        }
        return { lifecycle, config };
    }
    const versionOrder = compareStrictSemver(lifecycle.configVersion, contract.configVersion);
    if (versionOrder === null || versionOrder < 0) {
        throw new Error(`${tool.toolId}.configVersion baseline ${contract.configVersion} veya yeni olmalıdır.`);
    }
    assertEducationScenarioKind(config, tool.toolId, versionOrder === 0 ? 'example' : 'institution');
    assertRemoteLifecycleActive(tool, lifecycle, options.currentDate);
    return { lifecycle, config };
}
function dateTimeCoreConfig(tool: ToolDefinition, contract: FrozenContract, config: ToolConfig, lifecycle: Lifecycle): CalendarConfig | DurationConfig | DateAdjustmentConfig | undefined {
    const metadata = metadataFor(tool, contract, lifecycle);
    switch (config.kind) {
        case 'calendar-date-v1':
            return { metadata, minYear: config.minYear, maxYear: config.maxYear };
        case 'duration-v1':
            return { metadata, maxHours: config.maxHours, maxMinutes: config.maxMinutes };
        case 'date-adjustment-v1':
            return {
                metadata,
                minYear: config.minYear,
                maxYear: config.maxYear,
                maxYears: config.maxYears,
                maxMonths: config.maxMonths,
                maxWeeks: config.maxWeeks,
                maxDays: config.maxDays,
            };
    }
}
function educationCoreConfig(tool: ToolDefinition, contract: FrozenContract, config: ToolConfig, lifecycle: Lifecycle): RelativeGradeConfig | AssessmentConfig | ReportCardConfig | GradeAverageConfig | undefined {
    const metadata = metadataFor(tool, contract, lifecycle);
    switch (config.kind) {
        case 'relative-system-grade-v1':
            return { metadata, maxScore: config.maxScore, maxStandardDeviation: config.maxStandardDeviation, tScoreCenter: config.tScoreCenter, tScoreScale: config.tScoreScale, gradeScenario: config.gradeScenario };
        case 'weighted-assessment-v2':
            return { metadata, maxScore: config.maxScore, weightTotal: config.weightTotal, weightTolerance: config.weightTolerance };
        case 'report-card-average-v1':
            return { metadata, maxScore: config.maxScore, maxCourses: config.maxCourses, maxWeeklyHours: config.maxWeeklyHours };
        case 'grade-average-v2':
            return { metadata, maxScore: config.maxScore, maxCourses: config.maxCourses, maxCredits: config.maxCredits, weightedGradeScenario: config.weightedGradeScenario, unweightedBandScenario: config.unweightedBandScenario };
    }
}
function inputRecord(value: unknown): Readonly<Record<string, unknown>> {
    return isRecord(value) ? value : {};
}
function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
    return value !== null && typeof value === 'object' && !isArray(value);
}
function pickedInput(value: unknown, fields: readonly string[]) {
    const source = inputRecord(value);
    return Object.fromEntries(fields.map((field) => [field, source[field]]));
}
function receiptFor(tool: ToolDefinition, contract: FrozenContract, lifecycle: Lifecycle, result: Calculation<unknown>) {
    if (result.metadata.calculatorId !== contract.calculatorId ||
        result.metadata.version !== lifecycle.configVersion ||
        result.metadata.effectiveFrom !== lifecycle.effectiveFrom ||
        result.metadata.lastVerifiedAt !== lifecycle.lastReviewed) {
        throw new Error(`${tool.toolId} sonuç metadata'sı çalıştırılan config ile eşleşmiyor.`);
    }
    const sourceIds = tool.sources.map((source) => source.sourceId);
    if (!sameProjection(result.metadata.sources.map((source) => source.id), sourceIds)) {
        throw new Error(`${tool.toolId} sonuç kaynakları registry ile eşleşmiyor.`);
    }
    return {
        runtimeToolId: tool.toolId,
        calculatorId: contract.calculatorId,
        engineId: contract.engineId,
        configVersion: lifecycle.configVersion,
        effectiveFrom: lifecycle.effectiveFrom,
        lastReviewed: lifecycle.lastReviewed,
        reviewAfter: lifecycle.reviewAfter,
        sourceIds,
        resultMetadata: result.metadata,
    };
}
export function dateTimeConfigFor(tool: ToolDefinition) {
    const contract = contractFor(tool, 'date-time');
    const lifecycle = baselineLifecycle(tool);
    assertLifecycleShape(lifecycle, `${tool.toolId}.baseline`);
    return dateTimeCoreConfig(tool, contract, tool.config, lifecycle);
}
export function dateTimeInputFor(tool: ToolDefinition, input: unknown) {
    const contract = contractFor(tool, 'date-time');
    return pickedInput(input, contract.input.map(([id]) => id));
}
export function educationConfigFor(tool: ToolDefinition, options: Options) {
    const contract = contractFor(tool, 'education');
    const resolved = educationDefinitionFor(tool, contract, options);
    return educationCoreConfig(tool, contract, resolved.config, resolved.lifecycle);
}
export function educationInputFor(tool: ToolDefinition, input: unknown) {
    const contract = contractFor(tool, 'education');
    return pickedInput(input, contract.input.map(([id]) => id));
}
export function calculateDateTimeTool(tool: ToolDefinition, input: unknown) {
    const contract = contractFor(tool, 'date-time');
    const lifecycle = baselineLifecycle(tool);
    assertLifecycleShape(lifecycle, `${tool.toolId}.baseline`);
    const config = dateTimeCoreConfig(tool, contract, tool.config, lifecycle);
    const normalizedInput = dateTimeInputFor(tool, input);
    let result: Calculation<DateTimeValue>;
    switch (tool.toolId) {
        case 'age':
            if (!config || !('minYear' in config))
                throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
            result = calculateAge(normalizedInput, config);
            break;
        case 'date-difference':
            if (!config || !('minYear' in config))
                throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
            result = calculateDateDifference(normalizedInput, config);
            break;
        case 'duration':
            if (!config || !('maxHours' in config))
                throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
            result = calculateDuration(normalizedInput, config);
            break;
        case 'date-adjustment':
            if (!config || !('maxYears' in config))
                throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
            result = calculateDateAdjustment(normalizedInput, config);
            break;
        case 'iso-week':
            if (!config || !('minYear' in config))
                throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
            result = calculateIsoWeek(normalizedInput, config);
            break;
        default: throw new Error(`${tool.toolId} date-time dispatch sözleşmesinde yok.`);
    }
    return { result, receipt: receiptFor(tool, contract, lifecycle, result) };
}
export function calculateEducationTool(tool: ToolDefinition, input: unknown, options: Options) {
    const contract = contractFor(tool, 'education');
    const resolved = educationDefinitionFor(tool, contract, options);
    const config = educationCoreConfig(tool, contract, resolved.config, resolved.lifecycle);
    const normalizedInput = educationInputFor(tool, input);
    let result: Calculation<EducationValue>;
    switch (tool.toolId) {
        case 'relative-grade':
            if (!config || !('maxStandardDeviation' in config))
                throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
            result = calculateRelativeSystemGrade(normalizedInput, config);
            break;
        case 'report-card-average':
            if (!config || !('maxWeeklyHours' in config))
                throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
            result = calculateReportCardAverage(normalizedInput, config);
            break;
        case 'grade-average':
            if (!config || !('maxCredits' in config))
                throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
            result = calculateGradeAverage(normalizedInput, config);
            break;
        case 'required-final-score':
            if (!config || !('weightTotal' in config))
                throw new Error(`${tool.toolId} core config sözleşmesi eşleşmiyor.`);
            result = calculateRequiredFinalScore(normalizedInput, config);
            break;
        default: throw new Error(`${tool.toolId} education dispatch sözleşmesinde yok.`);
    }
    return { result, receipt: receiptFor(tool, contract, resolved.lifecycle, result) };
}
